/**
 * PlanLimitsManager
 *
 * Singleton that holds the current user's effective plan limits and
 * provides easy check methods for quota enforcement and feature gating.
 *
 * Follows the same static-class pattern as PermissionManager.
 *
 * Usage:
 *   PlanLimitsManager.init(planData, overrides);
 *   PlanLimitsManager.canCreateScreen(currentScreenCount);
 *   PlanLimitsManager.hasFeature("analytics");
 *   PlanLimitsManager.getLimit("max_screens");
 */

import {
    ALL_PLAN_FIELDS,
    PLAN_FIELD_MAP,
    FEATURE_TO_PAGE_MAP,
    PAGE_TO_FEATURE_MAP,
} from "../data/planSchema";

class PlanLimitsManager {

    // -----------------------------------------------------------------------
    // State
    // -----------------------------------------------------------------------

    /** Raw plan object from API (e.g. from get_subscriptionplans) */
    static planData = null;

    /** Per-user overrides set by sales — takes precedence over planData */
    static overrides = null;

    /** Merged effective limits (planData + overrides + defaults) */
    static effectiveLimits = {};

    /** Whether the manager has been initialised */
    static initialized = false;

    // -----------------------------------------------------------------------
    // Initialisation
    // -----------------------------------------------------------------------

    /**
     * Initialise (or re-initialise) with plan data and optional overrides.
     *
     * @param {Object|null} planData   – the plan object from the API
     * @param {Object|null} overrides  – per-user overrides (from sales deals)
     */
    static init = (planData = null, overrides = null) => {
        PlanLimitsManager.planData = planData;
        PlanLimitsManager.overrides = overrides;
        PlanLimitsManager.effectiveLimits = PlanLimitsManager._mergeLimits(planData, overrides);
        PlanLimitsManager.initialized = true;

        console.log("[PlanLimitsManager] Initialised with effective limits:", PlanLimitsManager.effectiveLimits);
    };

    /**
     * Reset the manager (e.g. on logout).
     */
    static reset = () => {
        PlanLimitsManager.planData = null;
        PlanLimitsManager.overrides = null;
        PlanLimitsManager.effectiveLimits = {};
        PlanLimitsManager.initialized = false;
    };

    // -----------------------------------------------------------------------
    // Limit resolution  (override > plan > schema default)
    // -----------------------------------------------------------------------

    /**
     * Merge plan data with overrides, falling back to schema defaults.
     * Handles the existing API field aliases (e.g. `screens_limit` → `max_screens`).
     */
    static _mergeLimits = (planData, overrides) => {
        const result = {};

        ALL_PLAN_FIELDS.forEach((field) => {
            const { key, defaultValue, apiAlias } = field;

            // 1. Check override first
            if (overrides && key in overrides) {
                result[key] = overrides[key];
                return;
            }

            // 2. Check planData under the canonical key
            if (planData && key in planData) {
                result[key] = planData[key];
                return;
            }

            // 3. Check planData under legacy API alias
            if (apiAlias && planData && apiAlias in planData) {
                result[key] = PlanLimitsManager._normaliseAliasValue(key, planData[apiAlias]);
                return;
            }

            // 4. Fall back to schema default
            result[key] = defaultValue;
        });

        return result;
    };

    /**
     * Normalise values from legacy API aliases.
     * e.g. `storage_limit` may come as "5 GB" string → convert to MB number.
     */
    static _normaliseAliasValue = (key, value) => {
        if (key === "max_storage_mb" && typeof value === "string") {
            // Parse "5 GB" → 5120, "500 MB" → 500
            const match = value.match(/([\d.]+)\s*(GB|MB|TB)/i);
            if (match) {
                const num = parseFloat(match[1]);
                const unit = match[2].toUpperCase();
                if (unit === "GB") return Math.round(num * 1024);
                if (unit === "TB") return Math.round(num * 1024 * 1024);
                return Math.round(num); // MB
            }
            return 0; // can't parse → unlimited
        }
        return value;
    };

    // -----------------------------------------------------------------------
    // Public API — Get raw limit values
    // -----------------------------------------------------------------------

    /**
     * Get the effective limit value for a given key.
     * @param {string} key — one of the keys from planSchema
     * @returns {*} the effective value
     */
    static getLimit = (key) => {
        if (key in PlanLimitsManager.effectiveLimits) {
            return PlanLimitsManager.effectiveLimits[key];
        }
        const field = PLAN_FIELD_MAP[key];
        return field ? field.defaultValue : undefined;
    };

    /**
     * Get formatted display value for a limit.
     * @param {string} key
     * @returns {string}
     */
    static getLimitDisplay = (key) => {
        const value = PlanLimitsManager.getLimit(key);
        const field = PLAN_FIELD_MAP[key];

        if (!field) return String(value);

        if (field.type === "number") {
            if (value === 0) return "Unlimited";
            if (key === "max_storage_mb") {
                if (value >= 1024) return `${(value / 1024).toFixed(1)} GB`;
                return `${value} MB`;
            }
            return String(value);
        }
        if (field.type === "boolean") {
            return value ? "Enabled" : "Disabled";
        }
        return String(value);
    };

    // -----------------------------------------------------------------------
    // Public API — Feature checks (boolean)
    // -----------------------------------------------------------------------

    /**
     * Check if a feature is enabled by feature key.
     * @param {string} featureKey — e.g. "feature_analytics"
     * @returns {boolean}
     */
    static hasFeature = (featureKey) => {
        const val = PlanLimitsManager.getLimit(featureKey);
        return !!val;
    };

    /**
     * Check if a page ID is enabled by the plan.
     * Uses the PAGE_TO_FEATURE_MAP to map Constants page IDs to feature keys.
     * If the page has no feature gate, returns true (always allowed).
     *
     * @param {string} pageId — e.g. "analytics", "campaign"
     * @returns {boolean}
     */
    static isPageEnabled = (pageId) => {
        const featureKey = PAGE_TO_FEATURE_MAP[pageId];
        if (!featureKey) return true; // no gate → always on
        return PlanLimitsManager.hasFeature(featureKey);
    };

    // -----------------------------------------------------------------------
    // Public API — Quota checks
    // -----------------------------------------------------------------------

    /**
     * Generic quota check. Returns true if the current count is below the limit.
     * A limit of 0 means unlimited.
     *
     * @param {string} quotaKey — e.g. "max_screens"
     * @param {number} currentCount — current usage count
     * @returns {boolean}
     */
    static isWithinQuota = (quotaKey, currentCount) => {
        const limit = PlanLimitsManager.getLimit(quotaKey);
        if (limit === 0 || limit === undefined || limit === null) return true; // unlimited
        return currentCount < limit;
    };

    /**
     * Get remaining quota.
     * @param {string} quotaKey
     * @param {number} currentCount
     * @returns {number|null} — null means unlimited
     */
    static getRemainingQuota = (quotaKey, currentCount) => {
        const limit = PlanLimitsManager.getLimit(quotaKey);
        if (limit === 0 || limit === undefined || limit === null) return null; // unlimited
        return Math.max(0, limit - currentCount);
    };

    // -- Convenience methods for common quota checks --

    /**
     * @param {number} currentCount — how many workspaces the user currently has
     */
    static canCreateWorkspace = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_workspaces", currentCount);
    };

    /**
     * @param {number} currentCount — how many locations in the current workspace
     */
    static canCreateLocation = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_locations", currentCount);
    };

    /**
     * @param {number} currentCount — total screens across all workspaces
     */
    static canCreateScreen = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_screens", currentCount);
    };

    /**
     * @param {number} currentCount — screens in the current location
     */
    static canAddScreenToLocation = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_screens_per_location", currentCount);
    };

    /**
     * Check if a file can be uploaded given current storage usage and file size.
     *
     * @param {number} currentStorageMB — current total storage used in MB
     * @param {number} fileSizeMB       — size of the file to upload in MB
     * @returns {{ allowed: boolean, reason: string|null }}
     */
    static canUploadFile = (currentStorageMB, fileSizeMB) => {
        const maxStorage = PlanLimitsManager.getLimit("max_storage_mb");
        const maxFileSize = PlanLimitsManager.getLimit("max_file_size_mb");

        // Check single-file size limit
        if (maxFileSize > 0 && fileSizeMB > maxFileSize) {
            return {
                allowed: false,
                reason: `File size (${fileSizeMB.toFixed(1)} MB) exceeds the maximum allowed file size (${maxFileSize} MB).`,
            };
        }

        // Check total storage limit
        if (maxStorage > 0 && (currentStorageMB + fileSizeMB) > maxStorage) {
            return {
                allowed: false,
                reason: `Adding this file would exceed your storage limit (${PlanLimitsManager.getLimitDisplay("max_storage_mb")}).`,
            };
        }

        return { allowed: true, reason: null };
    };

    /**
     * @param {number} currentCount — how many team members exist
     */
    static canInviteUser = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_users", currentCount);
    };

    /**
     * @param {number} currentCount — how many custom roles exist
     */
    static canCreateRole = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_roles", currentCount);
    };

    /**
     * @param {number} currentCount — how many playlists exist
     */
    static canCreatePlaylist = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_playlists", currentCount);
    };

    /**
     * @param {number} currentCount — how many active campaigns exist
     */
    static canCreateCampaign = (currentCount) => {
        return PlanLimitsManager.isWithinQuota("max_campaigns", currentCount);
    };

    // -----------------------------------------------------------------------
    // Public API — Usage summary (for dashboards / usage bars)
    // -----------------------------------------------------------------------

    /**
     * Get a usage summary object for a quota key.
     * @param {string} quotaKey
     * @param {number} currentCount
     * @returns {{ current, limit, remaining, percentage, isUnlimited, display }}
     */
    static getUsageSummary = (quotaKey, currentCount) => {
        const limit = PlanLimitsManager.getLimit(quotaKey);
        const isUnlimited = limit === 0 || limit === undefined || limit === null;

        return {
            current: currentCount,
            limit: isUnlimited ? null : limit,
            remaining: isUnlimited ? null : Math.max(0, limit - currentCount),
            percentage: isUnlimited ? 0 : Math.min(100, Math.round((currentCount / limit) * 100)),
            isUnlimited,
            display: isUnlimited
                ? `${currentCount} / Unlimited`
                : `${currentCount} / ${limit}`,
        };
    };
}

export default PlanLimitsManager;
